import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import {
  Activity,
  BookOpen,
  Boxes,
  Database,
  Eye,
  Globe2,
  Image as ImageIcon,
  Library,
  RefreshCw,
  ScrollText,
  Users
} from 'lucide-react';
import { divIcon, latLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CatalogItem, CollectionRecord, CreatorAuthority } from '../../catalog/types';
import { catalogService } from './services/catalogService';
import {
  fetchAdminAnalyticsEvents,
  isSupabaseAnalyticsEnabled,
  recordAdminAnalyticsEvent
} from '../../services/supabaseAnalytics';
import { parseBrowser, parseDevice, fetchClientGeo, sessionId } from './services/analyticsUtils';

type DashboardData = {
  items: CatalogItem[];
  creators: CreatorAuthority[];
  collections: CollectionRecord[];
};

type EventRow = {
  id?: string | number;
  event_type?: string;
  page?: string;
  label?: string;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
};

type RankedDatum = {
  label: string;
  value: number;
};

type MetricCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  note: string;
};

type MapPoint = RankedDatum & {
  lat: number;
  lng: number;
};

type VisitRow = {
  id: string | number;
  time: string;
  page: string;
  location: string;
  device: string;
  browser: string;
  referrer: string;
  eventName: string;
};

const MAP_LOOKUP: Record<string, { lat: number; lng: number }> = {
  london: { lat: 42.9849, lng: -81.2453 },
  'london, canada': { lat: 42.9849, lng: -81.2453 },
  'london, uk': { lat: 51.5072, lng: -0.1276 },
  'london, england': { lat: 51.5072, lng: -0.1276 },
  paris: { lat: 48.8566, lng: 2.3522 },
  rome: { lat: 41.9028, lng: 12.4964 },
  venice: { lat: 45.4408, lng: 12.3155 },
  berlin: { lat: 52.52, lng: 13.405 },
  amsterdam: { lat: 52.3676, lng: 4.9041 },
  madrid: { lat: 40.4168, lng: -3.7038 },
  vienna: { lat: 48.2082, lng: 16.3738 },
  toronto: { lat: 43.6532, lng: -79.3832 },
  montreal: { lat: 45.5019, lng: -73.5674 },
  'new york': { lat: 40.7128, lng: -74.006 },
  boston: { lat: 42.3601, lng: -71.0589 },
  chicago: { lat: 41.8781, lng: -87.6298 },
  washington: { lat: 38.9072, lng: -77.0369 },
  ottawa: { lat: 45.4215, lng: -75.6972 },
  philadelphia: { lat: 39.9526, lng: -75.1652 },
  'los angeles': { lat: 34.0549, lng: -118.2426 },
  'san francisco': { lat: 37.7749, lng: -122.4194 },
  vatican: { lat: 41.9029, lng: 12.4534 },
  oxford: { lat: 51.752, lng: -1.2577 },
  cambridge: { lat: 52.2053, lng: 0.1218 },
  prague: { lat: 50.0755, lng: 14.4378 },
  warsaw: { lat: 52.2297, lng: 21.0122 }
};

const markerIcon = divIcon({
  className: 'rarebooks-map-marker',
  html:
    '<div style="display:grid;place-items:center;width:28px;height:28px;border-radius:999px;background:#8b734e;border:2px solid #fff;color:#fff;font-size:12px;font-weight:700;box-shadow:0 4px 12px rgba(91,71,39,0.28)">•</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

function formatDate(value?: string) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizeLabel(value?: string | null) {
  if (!value || !value.trim()) {
    return 'Unspecified';
  }

  return value.trim();
}

function titleCase(value: string) {
  if (!value) {
    return value;
  }

  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function readMetadataValue(metadata: Record<string, unknown> | null | undefined, key: string) {
  const raw = metadata?.[key];
  return typeof raw === 'string' ? raw : '';
}

function countBy<T>(items: T[], getKey: (item: T) => string | undefined | null): RankedDatum[] {
  const bucket = new Map<string, number>();

  items.forEach((item) => {
    const key = normalizeLabel(getKey(item));
    bucket.set(key, (bucket.get(key) || 0) + 1);
  });

  return Array.from(bucket.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

function extractYear(value?: string) {
  if (!value) {
    return null;
  }

  const match = value.match(/\d{4}/);
  if (!match) {
    return null;
  }

  const parsed = Number.parseInt(match[0], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toDecadeLabel(year: number | null) {
  if (!year) {
    return 'Unknown';
  }

  const decade = Math.floor(year / 10) * 10;
  return `${decade}s`;
}

function getLocationLabel(item: CatalogItem) {
  const city = normalizeLabel(item.currentPhysicalLocation?.city || item.locationOfCreation?.city);
  const country = normalizeLabel(item.currentPhysicalLocation?.country || item.locationOfCreation?.country);

  if (city === 'Unspecified' && country === 'Unspecified') {
    return 'Unspecified';
  }

  if (city !== 'Unspecified' && country !== 'Unspecified') {
    return `${city}, ${country}`;
  }

  return city !== 'Unspecified' ? city : country;
}

function getLookupCity(item: CatalogItem) {
  const rawCity = normalizeLabel(
    item.currentPhysicalLocation?.city ||
      item.locationOfCreation?.city ||
      item.currentPhysicalLocation?.cityHistoricalName ||
      item.locationOfCreation?.cityHistoricalName
  );

  if (rawCity === 'Unspecified') {
    return rawCity;
  }

  const cityKey = rawCity
    .split(',')[0]
    .split('/')[0]
    .trim()
    .toLowerCase();

  const rawCountry = normalizeLabel(
    item.currentPhysicalLocation?.country || item.locationOfCreation?.country
  );

  if (rawCountry !== 'Unspecified') {
    const comboKey = `${cityKey}, ${rawCountry.toLowerCase()}`;
    if (MAP_LOOKUP[comboKey]) return comboKey;
  }

  return cityKey;
}

function getMapPointForItem(item: CatalogItem): MapPoint | null {
  const directCoordinates =
    item.currentPhysicalLocation?.coordinates || item.locationOfCreation?.coordinates;

  if (directCoordinates?.latitude != null && directCoordinates?.longitude != null) {
    return {
      label: getLocationLabel(item),
      value: 1,
      lat: directCoordinates.latitude,
      lng: directCoordinates.longitude
    };
  }

  const cityPart = getLookupCity(item);
  const mapped = MAP_LOOKUP[cityPart];

  if (!mapped) {
    return null;
  }

  return {
    label: getLocationLabel(item),
    value: 1,
    lat: mapped.lat,
    lng: mapped.lng
  };
}

function combineMapPoints(items: CatalogItem[]): MapPoint[] {
  const bucket = new Map<string, MapPoint>();

  items.forEach((item) => {
    const point = getMapPointForItem(item);
    if (!point) {
      return;
    }

    const key = `${point.label}-${point.lat.toFixed(3)}-${point.lng.toFixed(3)}`;
    const existing = bucket.get(key);

    if (existing) {
      existing.value += 1;
      return;
    }

    bucket.set(key, point);
  });

  return Array.from(bucket.values()).sort((a, b) => b.value - a.value).slice(0, 24);
}

function FitMapToPoints({ points }: { points: MapPoint[] }) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) {
      map.setView([20, 0], 2);
      return;
    }

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 5);
      return;
    }

    const bounds = latLngBounds(points.map((point) => [point.lat, point.lng]));
    map.fitBounds(bounds, { padding: [32, 32] });
  }, [map, points]);

  return null;
}

function MetricCard({ icon, label, value, note }: MetricCardProps) {
  return (
    <div className="rounded border border-[#d8c9af] bg-[#fcf6eb] px-4 py-3 shadow-[0_1px_0_rgba(109,82,39,0.06)]">
      <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#8a7451]">
        <span className="text-[#6c5532]">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-[28px] font-semibold leading-none text-[#5b4727]">{value}</div>
      <div className="mt-2 text-[11px] text-[#9b8766]">{note}</div>
    </div>
  );
}

function RankedPanel({
  title,
  data,
  emptyMessage
}: {
  title: string;
  data: RankedDatum[];
  emptyMessage: string;
}) {
  const maxValue = data[0]?.value || 1;

  return (
    <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
      <h3 className="mb-3 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">{title}</h3>
      {data.length === 0 ? (
        <p className="text-xs text-[#9b8766]">{emptyMessage}</p>
      ) : (
        <div className="space-y-2.5">
          {data.map((entry) => (
            <div key={entry.label} className="grid grid-cols-[minmax(0,1fr)_36px] items-center gap-3">
              <div>
                <div className="mb-1 flex items-center justify-between gap-3 text-[12px] text-[#6c5532]">
                  <span className="truncate">{entry.label}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#efe5d3]">
                  <div
                    className="h-full rounded-full bg-[#7e6844]"
                    style={{ width: `${Math.max(7, (entry.value / maxValue) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-right text-[11px] font-medium text-[#8a7451]">{entry.value}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// parseBrowser, parseDevice, fetchClientGeo, sessionId live in analyticsUtils
// — imported below to avoid duplication and keep geo logic in one place.

const AdminAnalytics: React.FC<{ onShowVisitors?: () => void }> = ({ onShowVisitors }) => {
  const [data, setData] = useState<DashboardData>({ items: [], creators: [], collections: [] });
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const [items, creators, collections, supabaseEvents] = await Promise.all([
        catalogService.getAllCatalogItems(1000),
        catalogService.getAllCreators(),
        catalogService.getAllCollections(),
        fetchAdminAnalyticsEvents(200)
      ]);

      setData({ items, creators, collections });
      setEvents(supabaseEvents);
    } catch (loadError) {
      console.error(loadError);
      setError('Could not load admin analytics. Check your Firebase indexes and network access.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    fetchClientGeo().then((geo) => {
      recordAdminAnalyticsEvent({
        eventType: 'admin_dashboard_view',
        label: 'dashboard_opened',
        metadata: {
          page: 'admin-analytics',
          browser: parseBrowser(navigator.userAgent),
          device: parseDevice(navigator.userAgent),
          referrer: document.referrer ? new URL(document.referrer).hostname : 'Direct',
          session_id: sessionId,
          location: geo.location,
          city: geo.city,
          region: geo.region,
          country: geo.country,
          country_code: geo.country_code,
          lat: geo.lat,
          lng: geo.lng,
        }
      });
    });
  }, []);

  const analytics = useMemo(() => {
    const { items, creators, collections } = data;

    const publishedCount = items.filter((item) => item.recordStatus === 'published').length;
    const averageCompleteness =
      items.length > 0
        ? Math.round(items.reduce((sum, item) => sum + (item.completenessScore || 0), 0) / items.length)
        : 0;

    const itemsWithImages = items.filter((item) => (item.images || []).length > 0).length;
    const itemsWithCreators = items.filter((item) => (item.creators || []).length > 0).length;
    const itemsWithSubjects = items.filter((item) => (item.subjects || []).length > 0).length;
    const itemsWithCollections = items.filter((item) => item.collectionId || item.collectionName).length;
    const itemsWithPublicAccess = items.filter((item) => item.visibilityLevel === 'public').length;

    const recentItems = [...items]
      .sort((a, b) => new Date(b.lastModified || 0).getTime() - new Date(a.lastModified || 0).getTime())
      .slice(0, 14);

    const lowCompleteness = [...items]
      .filter((item) => (item.completenessScore || 0) < 60)
      .sort((a, b) => (a.completenessScore || 0) - (b.completenessScore || 0))
      .slice(0, 8);

    const locationBreakdown = countBy(items, (item) => getLocationLabel(item));
    const cityBreakdown = countBy(items, (item) => item.currentPhysicalLocation?.city || item.locationOfCreation?.city).slice(0, 6);
    const countryBreakdown = countBy(items, (item) => item.currentPhysicalLocation?.country || item.locationOfCreation?.country).slice(0, 6);
    const objectTypeBreakdown = countBy(items, (item) => item.objectType).slice(0, 6);
    const statusBreakdown = countBy(items, (item) => item.recordStatus);
    const creatorRoleBreakdown = countBy(creators, (creator) => creator.primaryRole).slice(0, 6);
    const languageBreakdown = countBy(
      items.flatMap((item) => (item.language || []).map((language: string) => ({ language }))),
      (entry) => entry.language
    ).slice(0, 6);
    const visibilityBreakdown = countBy(items, (item) => item.visibilityLevel);
    const decadeBreakdown = countBy(items, (item) => toDecadeLabel(extractYear(item.dateOfPublication))).slice(0, 10);

    const visitRows: VisitRow[] =
      events.length > 0
        ? events.map((event) => {
            const metadata = event.metadata || {};
            return {
              id: event.id || `${event.created_at}-${event.event_type}`,
              time: formatDate(event.created_at),
              page: normalizeLabel(event.page || readMetadataValue(metadata, 'page') || '/admin'),
              location: normalizeLabel(readMetadataValue(metadata, 'location') || readMetadataValue(metadata, 'city') || 'Unknown'),
              device: normalizeLabel(readMetadataValue(metadata, 'device') || 'Unknown'),
              browser: normalizeLabel(readMetadataValue(metadata, 'browser') || 'Unknown'),
              referrer: normalizeLabel(readMetadataValue(metadata, 'referrer') || 'Direct'),
              eventName: normalizeLabel(event.event_type || 'event')
            };
          })
        : recentItems.map((item, index) => ({
            id: item.id || index,
            time: formatDate(item.lastModified),
            page: `/catalog-items/${item.id || 'draft'}`,
            location: getLocationLabel(item),
            device: 'Catalog',
            browser: 'Firebase',
            referrer: 'Direct',
            eventName: titleCase(item.recordStatus || 'updated')
          }));

    const topPages = countBy(visitRows, (row) => row.page).slice(0, 6);
    const referrers = countBy(visitRows, (row) => row.referrer).slice(0, 8);
    const devices = countBy(visitRows, (row) => row.device).slice(0, 6);
    const browsers = countBy(visitRows, (row) => row.browser).slice(0, 6);
    const eventTypes = countBy(visitRows, (row) => row.eventName).slice(0, 8);

    return {
      itemsCount: items.length,
      creatorsCount: creators.length,
      collectionsCount: collections.length,
      publishedCount,
      averageCompleteness,
      itemsWithImages,
      itemsWithCreators,
      itemsWithSubjects,
      itemsWithCollections,
      itemsWithPublicAccess,
      recentItems,
      lowCompleteness,
      locationBreakdown,
      cityBreakdown,
      countryBreakdown,
      objectTypeBreakdown,
      statusBreakdown,
      creatorRoleBreakdown,
      languageBreakdown,
      visibilityBreakdown,
      decadeBreakdown,
      topPages,
      referrers,
      devices,
      browsers,
      eventTypes,
      visitRows,
      mapPoints: combineMapPoints(items)
    };
  }, [data, events]);

  return (
    <div className="min-h-screen bg-[#f3eadc] text-[#5b4727]">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between border-b border-[#d8c9af] pb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#907a57]">Rare Book Catalog</p>
            <h1 className="mt-1 text-xl font-semibold tracking-[0.06em]">Analysis Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right text-[11px] text-[#9b8766] sm:block">
              <div>{isSupabaseAnalyticsEnabled() ? 'Supabase tracking active' : 'Supabase tracking optional'}</div>
              <div>{data.items.length} records loaded</div>
            </div>
            {onShowVisitors && (
              <button
                onClick={onShowVisitors}
                className="inline-flex items-center gap-2 rounded border border-[#d8c9af] bg-[#fff9ef] px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-[#6c5532] hover:bg-[#f8efdf]"
              >
                <Users size={14} />
                Visitors
              </button>
            )}
            <button
              onClick={loadDashboard}
              className="inline-flex items-center gap-2 rounded border border-[#d8c9af] bg-[#fff9ef] px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-[#6c5532] hover:bg-[#f8efdf]"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded border border-[#d8c9af] bg-[#fffaf2] px-6 py-12 text-center text-sm text-[#8a7451]">
            <RefreshCw size={18} className="mx-auto mb-3 animate-spin" />
            Loading analytics...
          </div>
        ) : (
          <div className="space-y-4">
            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                  <Activity size={14} />
                  <span>Analysis Dashboard</span>
                </div>
                <div className="text-[11px] text-[#9b8766]">Updated {formatDate(new Date().toISOString())}</div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                  icon={<Library size={14} />}
                  label="Catalog Items"
                  value={analytics.itemsCount}
                  note={`${analytics.publishedCount} published`}
                />
                <MetricCard
                  icon={<Users size={14} />}
                  label="Creator Records"
                  value={analytics.creatorsCount}
                  note="authority entries"
                />
                <MetricCard
                  icon={<Boxes size={14} />}
                  label="Collections"
                  value={analytics.collectionsCount}
                  note={`${analytics.itemsWithCollections} linked items`}
                />
                <MetricCard
                  icon={<Database size={14} />}
                  label="Avg Completeness"
                  value={`${analytics.averageCompleteness}%`}
                  note={`${analytics.lowCompleteness.length} need attention`}
                />
                <MetricCard
                  icon={<ImageIcon size={14} />}
                  label="Image Coverage"
                  value={`${analytics.itemsWithImages}/${analytics.itemsCount}`}
                  note={`${analytics.itemsWithPublicAccess} public items`}
                />
              </div>
            </section>

            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
              <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                <ScrollText size={14} />
                <span>Publication Timeline</span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 lg:grid-cols-10">
                {analytics.decadeBreakdown.length === 0 ? (
                  <p className="col-span-full text-sm text-[#9b8766]">No publication-date data available yet.</p>
                ) : (
                  analytics.decadeBreakdown.map((entry) => {
                    const max = analytics.decadeBreakdown[0]?.value || 1;
                    return (
                      <div key={entry.label} className="rounded border border-[#eadfc9] bg-[#fcf6eb] px-2 py-3 text-center">
                        <div className="mx-auto mb-2 flex h-16 w-6 items-end rounded-full bg-[#efe5d3]">
                          <div
                            className="w-full rounded-full bg-[#7e6844]"
                            style={{ height: `${Math.max(10, (entry.value / max) * 100)}%` }}
                          />
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#8a7451]">{entry.label}</div>
                        <div className="mt-1 text-[11px] font-medium text-[#5b4727]">{entry.value}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
              <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                <Globe2 size={14} />
                <span>Current Locations</span>
              </div>
              <div className="overflow-hidden rounded border border-[#e6d8c0]" style={{ minHeight: 300 }}>
                {analytics.mapPoints.length === 0 ? (
                  <div className="grid min-h-[300px] place-items-center bg-[#eef1ee] text-sm text-[#8a7451]">
                    No mapped coordinates available yet.
                  </div>
                ) : (
                  <MapContainer
                    center={[20, 0]}
                    zoom={2}
                    scrollWheelZoom={true}
                    style={{ height: 300, width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitMapToPoints points={analytics.mapPoints} />
                    {analytics.mapPoints.map((point) => (
                      <Marker
                        key={`${point.label}-${point.lat}-${point.lng}`}
                        position={[point.lat, point.lng]}
                        icon={markerIcon}
                      >
                        <Popup>
                          <div style={{ minWidth: 140 }}>
                            <div style={{ fontWeight: 600 }}>{point.label}</div>
                            <div>{point.value} item{point.value === 1 ? '' : 's'}</div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                )}
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <RankedPanel title="Countries" data={analytics.countryBreakdown} emptyMessage="No country metadata found." />
              <RankedPanel title="Cities" data={analytics.cityBreakdown} emptyMessage="No city metadata found." />
              <RankedPanel title="Object Types" data={analytics.objectTypeBreakdown} emptyMessage="No object types found." />
              <RankedPanel title="Languages" data={analytics.languageBreakdown} emptyMessage="No languages recorded." />
              <RankedPanel title="Creator Roles" data={analytics.creatorRoleBreakdown} emptyMessage="No creator authority records." />
              <RankedPanel title="Record Status" data={analytics.statusBreakdown} emptyMessage="No status data yet." />
            </section>

            <section className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
                <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                  <Eye size={14} />
                  <span>Coverage Summary</span>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Image coverage', value: analytics.itemsWithImages, total: analytics.itemsCount },
                    { label: 'Creator linkage', value: analytics.itemsWithCreators, total: analytics.itemsCount },
                    { label: 'Subject coverage', value: analytics.itemsWithSubjects, total: analytics.itemsCount },
                    { label: 'Public visibility', value: analytics.itemsWithPublicAccess, total: analytics.itemsCount }
                  ].map((row) => {
                    const total = Math.max(row.total, 1);
                    const percent = Math.round((row.value / total) * 100);
                    return (
                      <div key={row.label}>
                        <div className="mb-1 flex items-center justify-between text-[12px] text-[#6c5532]">
                          <span>{row.label}</span>
                          <span>{row.value} / {row.total}</span>
                        </div>
                        <div className="h-3 overflow-hidden rounded-full bg-[#efe5d3]">
                          <div className="h-full rounded-full bg-[#7e6844]" style={{ width: `${Math.max(6, percent)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
                <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                  <BookOpen size={14} />
                  <span>Low Completeness Records</span>
                </div>
                {analytics.lowCompleteness.length === 0 ? (
                  <p className="text-sm text-[#9b8766]">No low-completeness records found.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.lowCompleteness.map((item) => (
                      <div key={item.id} className="rounded border border-[#eadfc9] bg-[#fcf6eb] px-3 py-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-medium text-[#5b4727]">{item.titleProper || 'Untitled item'}</div>
                            <div className="mt-1 text-[11px] text-[#8a7451]">
                              {normalizeLabel(item.objectType)} · {normalizeLabel(item.recordStatus)}
                            </div>
                          </div>
                          <div className="rounded bg-[#efe5d3] px-2 py-1 text-[11px] font-medium text-[#6c5532]">
                            {item.completenessScore || 0}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </section>

            <section className="grid gap-4 md:grid-cols-2">
              <RankedPanel title="Top Pages" data={analytics.topPages} emptyMessage="No page data available yet." />
              <RankedPanel title="Referrers" data={analytics.referrers} emptyMessage="No referrer data available yet." />
              <RankedPanel title="Devices" data={analytics.devices} emptyMessage="No device data available yet." />
              <RankedPanel title="Browsers" data={analytics.browsers} emptyMessage="No browser data available yet." />
            </section>

            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                  <Activity size={14} />
                  <span>Events</span>
                </div>
                <div className="text-[11px] text-[#9b8766]">
                  {events.length > 0 ? 'Supabase event metadata' : 'Fallback event summary from catalog updates'}
                </div>
              </div>
              <div className="mb-4">
                <div className="space-y-2.5">
                  {analytics.eventTypes.map((entry) => {
                    const max = analytics.eventTypes[0]?.value || 1;
                    return (
                      <div key={entry.label} className="grid grid-cols-[minmax(0,1fr)_40px] items-center gap-3">
                        <div>
                          <div className="mb-1 flex items-center justify-between text-[12px] text-[#6c5532]">
                            <span>{entry.label}</span>
                          </div>
                          <div className="h-3 overflow-hidden rounded-full bg-[#efe5d3]">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-[#3561b8] via-[#7b8f95] to-[#ffd83d]"
                              style={{ width: `${Math.max(8, (entry.value / max) * 100)}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-right text-[11px] font-medium text-[#8a7451]">{entry.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8a7451]">
                  <Activity size={14} />
                  <span>Recent Visits</span>
                </div>
                <div className="text-[11px] text-[#9b8766]">
                  {events.length > 0 ? 'Recent analytics events with metadata' : 'Fallback rows from recent catalog activity'}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#e6d8c0] text-[10px] uppercase tracking-[0.18em] text-[#8a7451]">
                      <th className="px-3 py-2 font-medium">Time</th>
                      <th className="px-3 py-2 font-medium">Page</th>
                      <th className="px-3 py-2 font-medium">Location</th>
                      <th className="px-3 py-2 font-medium">Device</th>
                      <th className="px-3 py-2 font-medium">Browser</th>
                      <th className="px-3 py-2 font-medium">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.visitRows.map((row) => (
                      <tr key={row.id} className="border-b border-[#f0e6d6] text-[12px] text-[#5b4727]">
                        <td className="px-3 py-2 whitespace-nowrap">{row.time}</td>
                        <td className="px-3 py-2">{row.page}</td>
                        <td className="px-3 py-2">{row.location}</td>
                        <td className="px-3 py-2">{row.device}</td>
                        <td className="px-3 py-2">{row.browser}</td>
                        <td className="px-3 py-2">{row.referrer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded border border-[#d8c9af] bg-[#fffaf2] p-4 text-[12px] leading-6 text-[#7d6948]">
              <div className="mb-2 font-medium uppercase tracking-[0.18em] text-[#8a7451]">Supabase Hookup</div>
              <p>
                This dashboard already runs from Firebase catalog data. If you want the bottom event feed to behave like a true
                analytics log, keep `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_ANON_KEY` in your `.env` and create the
                `admin_analytics_events` table described earlier.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
